import React, { useState, useMemo } from "react";
import { useFormikContext } from "formik";
import HoverIconButton from "components/HoverIconButton";
import FieldError from "components/FieldError";
import { useEditor, EditorContent } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import Mention from "@tiptap/extension-mention";
import get from "lodash/get";
import { proseMirrorStyles } from "constants/styles";
import VariableMenu from "./VariableMenu";
import { getFieldPath } from "utils/form";
import { COLORS } from "utils/styles";
import debounce from "lodash/debounce";
import { DEFAULT_DEBOUNCE } from "utils/constants/form";
const ACTION_SIZE = 14;

const ExtendedMention = Mention.extend({
  addAttributes() {
    return {
      uuid: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-uuid"),
        renderHTML: (attributes) => {
          if (!attributes.uuid) {
            return {};
          }

          return {
            "data-uuid": attributes.uuid
          };
        }
      },
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-label"),
        renderHTML: (attributes) => {
          if (!attributes.label) {
            return {};
          }

          return {
            "data-label": attributes.label
          };
        }
      },
      model: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-model"),
        renderHTML: (attributes) => {
          if (!attributes.model) {
            return {};
          }

          return {
            "data-model": attributes.model
          };
        }
      }
    };
  }
});

const EditorField = ({ content, onUpdate, name }) => {
  const [showMenu, setShowMenu] = useState(false);

  const editor = useEditor({
    onFocus({ editor, event }) {
      setShowMenu(true);
    },
    onBlur({ editor, event }) {
      // setShowMenu(false);
    },
    onUpdate,
    extensions: [
      Document,
      Paragraph,
      Text,
      Placeholder.configure({
        placeholder: "Enter text or insert data..."
      }),
      ExtendedMention.configure({
        HTMLAttributes: {
          class: "mention"
        },
        renderLabel({ options, node }) {
          return node.attrs.label;
        }
      })
    ],
    content: content
  });

  return (
    <div className="relative">
      <EditorContent editor={editor} />
      {showMenu && (
        <VariableMenu editor={editor} setShowMenu={setShowMenu} name={name} />
      )}
    </div>
  );
};

// https://github.com/ueberdosis/tiptap/blob/main/packages/extension-mention/src/mention.ts
const WorkflowField = ({ name }) => {
  const {
    values,
    setFieldValue,
    setFieldTouched,
    touched,
    errors
  } = useFormikContext();
  const templateId = values.data.steps[0].config.templateId;
  const field = get(values, name);

  const valuePath = getFieldPath(name, "value");
  const valueTouched = get(touched, valuePath);
  const valueError = get(errors, valuePath);
  const showError = Boolean(valueTouched && valueError);

  const lockedPath = getFieldPath(name, "config.locked");
  const lockedValue = get(values, lockedPath);
  const lockedTouched = get(touched, lockedPath);

  const requiredPath = getFieldPath(name, "config.required");

  const onUpdate = ({ editor }) => {
    setFieldValue(valuePath, editor.getJSON());
    if (!valueTouched) {
      setTimeout(() => setFieldTouched(valuePath, true), 0);
    }
  };
  const debounceUpdate = useMemo(
    () => debounce(onUpdate, DEFAULT_DEBOUNCE),
    []
  );

  const content = field.value || {
    type: "doc",
    content: [
      {
        type: "paragraph"
      }
    ]
  };

  return (
    <div className="flex flex-column">
      <div className="flex flex-row items-center justify-between w-100">
        <label className="f6 fw6 db">{field.uid}</label>
        <div className="dib flex flex-row items-center">
          <HoverIconButton
            size={ACTION_SIZE}
            onClick={() => {
              setFieldValue(lockedPath, !lockedValue);
              if (!lockedTouched) {
                setFieldTouched(lockedPath, true);
              }
            }}
            color={lockedValue ? COLORS.primary : COLORS.blue}
            type={lockedValue ? "lock" : "unlock"}
          />
        </div>
      </div>
      <EditorField
        name={name}
        key={templateId}
        content={content}
        onUpdate={debounceUpdate}
      />
      <FieldError error={valueError} name={name} />
      <style jsx>{proseMirrorStyles}</style>
    </div>
  );
};

export default WorkflowField;
